/**
 * Copyright 2024 ElectricSQL and contributors.
 * Copyright 2026 Improve Labs.
 *
 * Licensed under the Apache License, Version 2.0 (the "License");
 * you may not use this file except in compliance with the License.
 * You may obtain a copy of the License at
 *
 *     http://www.apache.org/licenses/LICENSE-2.0
 *
 * Unless required by applicable law or agreed to in writing, software
 * distributed under the License is distributed on an "AS IS" BASIS,
 * WITHOUT WARRANTIES OR CONDITIONS OF ANY KIND, either express or implied.
 * See the License for the specific language governing permissions and
 * limitations under the License.
 *
 * `LeaderChangedError`'s NAME and MESSAGE are taken from PGlite v0.5.4
 * (commit 6787212), `packages/pglite/src/worker/index.ts` lines 738-742.
 *
 * MODIFICATIONS from the original:
 *   - Carries `isLeaderChangedError` as a structural type guard, because the
 *     error crosses a `postMessage` boundary in some paths and `instanceof`
 *     cannot survive that.
 *   - Adds `LeaderUnavailableError`, which PGlite has no analogue for (it has no
 *     handover queue and therefore no budget that can elapse).
 */

/** Marker property so a reconstructed (post-`structuredClone`) error is still classifiable. */
const LEADER_CHANGED_MARKER = "__haniverseLeaderChanged";
const LEADER_UNAVAILABLE_MARKER = "__haniverseLeaderUnavailable";

/**
 * A DISPATCHED request whose leader died before answering. The request is in an
 * INDETERMINATE state: the old leader may have applied it, partially applied it,
 * or never seen it.
 *
 * Deliberately NOT re-sent (settled decision D1: at-most-once for dispatched
 * requests). Re-sending would risk a double-apply on a non-idempotent write; the
 * durability half of the answer is the applied-ids ledger, which is a later
 * stage. Until then this error is the honest surface, and every rejection is
 * additionally reported via the `leader-lost-write` marker so the real-world
 * rate is measurable rather than guessed at.
 *
 * Callers classifying errors must NOT treat this as a lock/busy error: it is not
 * retryable by `retryOnDatabaseLocked`, and that is correct — a transaction
 * interrupted this way rolled back as a unit, so it failed honestly.
 */
export class LeaderChangedError extends Error {
  public readonly [LEADER_CHANGED_MARKER] = true;

  constructor(message = "Leader changed, pending operation in indeterminate state") {
    super(message);
    this.name = "LeaderChangedError";
  }
}

/**
 * No leader could be reached within the handover budget, or the queue overflowed.
 * Distinct from {@link LeaderChangedError}: nothing was ever dispatched, so there
 * is no indeterminacy — the request definitively did not run.
 */
export class LeaderUnavailableError extends Error {
  public readonly [LEADER_UNAVAILABLE_MARKER] = true;

  constructor(message: string) {
    super(message);
    this.name = "LeaderUnavailableError";
  }
}

/** Structural check that survives a `structuredClone` round trip (`instanceof` does not). */
export function isLeaderChangedError(error: unknown): boolean {
  if (error instanceof LeaderChangedError) {
    return true;
  }
  return (
    typeof error === "object" &&
    error !== null &&
    Reflect.get(error, LEADER_CHANGED_MARKER) === true
  );
}

/** Structural check that survives a `structuredClone` round trip. */
export function isLeaderUnavailableError(error: unknown): boolean {
  if (error instanceof LeaderUnavailableError) {
    return true;
  }
  return (
    typeof error === "object" &&
    error !== null &&
    Reflect.get(error, LEADER_UNAVAILABLE_MARKER) === true
  );
}
